import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

export type NotifySeverity = 'error' | 'success' | 'info'

export interface NotifyItem {
  id: number
  message: string
  severity: NotifySeverity
}

interface NotifyApi {
  show: (message: string, severity?: NotifySeverity) => void
}

// API (show) и текущее уведомление разнесены по двум контекстам: места вызова
// (страницы) подписаны только на стабильный show и не ре-рендерятся на каждом
// уведомлении; TopBar подписан на current и рендерит flip-баннер.
const NotifyApiCtx = createContext<NotifyApi | null>(null)
const NotifyDisplayCtx = createContext<NotifyItem | null>(null)

// Длительность показа одного уведомления — должна совпадать с длиной
// @keyframes header-flap в TopBar.css.
const SHOW_MS = 3200
// Короткая пауза между уведомлениями в очереди — чтобы flip-out успел доиграть
// до flip-in следующего.
const GAP_MS = 260

let nextId = 0

export function NotifyProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<NotifyItem | null>(null)
  const queueRef = useRef<NotifyItem[]>([])
  const activeRef = useRef(false)

  const show = useCallback<NotifyApi['show']>((message, severity = 'info') => {
    queueRef.current.push({ id: ++nextId, message, severity })
    // Показываем по одному: следующее из очереди стартует только после того, как
    // предыдущее отыграло SHOW_MS и выдержало паузу GAP_MS. Защита activeRef не
    // даёт запуститься второму циклу при нескольких show() подряд.
    function advance() {
      if (activeRef.current) return
      const next = queueRef.current.shift()
      if (!next) return
      activeRef.current = true
      setCurrent(next)
      setTimeout(() => {
        setCurrent(null)
        activeRef.current = false
        setTimeout(advance, GAP_MS)
      }, SHOW_MS)
    }
    advance()
  }, [])

  const api = useMemo(() => ({ show }), [show])

  return (
    <NotifyApiCtx.Provider value={api}>
      <NotifyDisplayCtx.Provider value={current}>{children}</NotifyDisplayCtx.Provider>
    </NotifyApiCtx.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotify(): NotifyApi {
  const ctx = useContext(NotifyApiCtx)
  if (!ctx) throw new Error('useNotify must be used inside <NotifyProvider>')
  return ctx
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotifyDisplay(): NotifyItem | null {
  return useContext(NotifyDisplayCtx)
}
